import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import LockRounded from '@mui/icons-material/LockRounded';

import { line, slate } from '../theme';

/** A screenshot in a browser chrome — a raw PNG on a dark page reads as a
 *  stray image; framed, it reads as software.
 *
 *  Extracted out of Nizro.js so the same treatment can be reused by the
 *  home-page teaser and the full /nizro page without copying the markup.
 *
 *  `url` puts an address bar in the chrome, so a Nizro screen visibly lives at
 *  nizro.app. `ratio` fixes the viewport's shape and crops the image to fill
 *  it, which keeps a set of screenshots the same size when they are swapped,
 *  and `focus` says which part to keep (an object-position, passed as an
 *  inline style so the RTL plugin cannot mirror "right" into "left").
 *  `children` replaces the single image (the showcase stacks several). */
export default function Framed({ src, alt, url, ratio, focus = 'top', sx, children }) {
  return (
    <Box
      sx={{
        // Tighter than the cards around it: a big screenshot in a 42px corner
        // loses its own corners, and a window reads as a window at this radius.
        borderRadius: '18px',
        overflow: 'hidden',
        border: `1px solid ${line}`,
        boxShadow: `0 30px 80px ${alpha('#000', 0.55)}`,
        bgcolor: '#0B1220',
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', px: 1.75, py: url ? 1 : 1.25, borderBottom: `1px solid ${line}` }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: url ? 22 : 0 }}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
            <Box key={c} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: alpha(c, 0.75) }} />
          ))}
        </Stack>
        {url && (
          <Stack
            direction="row"
            alignItems="center"
            dir="ltr"
            sx={{
              // gap, not spacing: the RTL plugin would move spacing's margin to
              // the wrong side of this deliberately left-to-right pill.
              gap: 0.5,
              // A sibling of the dots, not a Stack child: Stack's spacing rule
              // resets its children's margins, which would undo mx:auto. And
              // centred without left:50% + translateX, which the RTL plugin
              // would mirror into an off-centre right:50%.
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              mx: 'auto',
              width: 'fit-content',
              transform: 'translateY(-50%)',
              px: 1.5,
              py: 0.25,
              borderRadius: 999,
              bgcolor: alpha('#FFFFFF', 0.05),
              border: `1px solid ${line}`,
              color: slate[400],
            }}
          >
            <LockRounded sx={{ fontSize: 11 }} />
            <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.01em' }}>
              {url}
            </Typography>
          </Stack>
        )}
      </Box>
      {children ? (
        <Box sx={{ position: 'relative', aspectRatio: ratio ? String(ratio) : undefined }}>{children}</Box>
      ) : (
        <Box
          component="img"
          src={src}
          alt={alt}
          style={ratio ? { objectPosition: focus } : undefined}
          sx={{
            display: 'block',
            width: '100%',
            ...(ratio ? { aspectRatio: String(ratio), objectFit: 'cover' } : {}),
          }}
        />
      )}
    </Box>
  );
}
